import { computed, effect, inject, Injectable, Injector } from "@angular/core";
import { CorpForm } from "@corporate-tailwind/corp-form";
import { OrderWizardDto, OrderWizardSchema } from "../../model/order-wizard.model";
import { OrderWizardSelectionState } from "../_state/order-wizard.selection.state";
import { OrderWizardState } from "../_state/order-wizard.state";
import { CorpTranslateService } from "@corporate-tailwind/translation";
import { debounceTime, filter, Observable, startWith } from "rxjs";
import { FormArray } from "@angular/forms";
import { SessionUpdateRequest } from "src/app/shared/session/session-update-request.model";
import { PickupPointDto } from "../../model/pickup-point.model";
import { CustomerDto } from "../../model/customer.model";
import { CurrencyPurchaseDto } from "../../../front-pages/common/fp-order-panel/fp-order-purchase/model/currency-purchase.model";
import { EnumCurrency } from "../../model/currency.enum";
import { TranslateService } from "@ngx-translate/core";
import { nullifyIfEmpty } from "src/app/shared/helper/object-cleaner.helper";
import { CurrencyDirection } from "../../model/order-currency-direction.model";
import { ChangeObject } from "src/app/shared/model/change-object.model";
import { OrderDueDiligenceDto } from "../../model/order-due-diligence.model";
import { OrderGtcDto } from "../../model/order-gtc.model";
import { OrderIdentificationDto } from "../../model/order-identification.model";

@Injectable({ providedIn: 'root' })
export class OrderWizardService extends CorpForm<OrderWizardDto> {
  private previousValueStep01!: CurrencyPurchaseDto[] | null;
  private previousValueStep02: PickupPointDto | null = null;
  private previousValueStep03: OrderIdentificationDto | null = null;
  private previousValueStep04: OrderDueDiligenceDto | null = null;
  private previousValueStep05: OrderGtcDto | null = null;

  selection$ = computed(() => {
    this._corpTranslateService.lang$(); // dépendance langue
    return this._corpTranslateService.translate(this._selectionState.selection());
  });

  orderWizard$ = computed(() => this._state.orderWizard());
  loading$ = this._state.loading;
  loadingCurrencyValue$ = this._state.loadingCurrencyValue;
  loadingCurrencyCreation$ = this._state.loadingCurrencyCreation;
  
  loadingEmailValidation$ = this._state.loadingEmailValidation;
  emailValidationIsSent$ = this._state.emailValidationIsSent;
  emailValidationPoolingCountdown$ = this._state.emailValidationPoolingCountdown;
  emailValidationIsTimeOut$ = this._state.emailValidationIsTimeOut;
  
  //maj des 
  filteredListTarget = computed(() => {
      const orderWizard = this.orderWizard$(); // abonnement

      const cpList = this.getValue(x=>x.step01?.currencyPurchaseList) as CurrencyPurchaseDto[];
      const targetCodeSelectedList: string[] = [EnumCurrency.EUR];

      cpList.forEach(x=>{targetCodeSelectedList.push(x.targetCurrency?.code!); });
      if(targetCodeSelectedList.length != null && targetCodeSelectedList.length > 0 ){
          return this.selection$()?.currency!.filter(x=> !targetCodeSelectedList.includes(x.code!))
      }
      return this.selection$()?.currency;
    });

  constructor(
    public _selectionState: OrderWizardSelectionState,
    public _state: OrderWizardState,
    private _corpTranslateService: CorpTranslateService,
    public _translateService: TranslateService
  ) {

    super(OrderWizardSchema, OrderWizardSchema)

    // 🔄 synchroniser le signal -> form
    var effectSignal = effect(() => {
      const value = this._state.orderWizard();

      console.log('value', value);
      
      if (!value) return;

      // console.log('--> signal change', value);
      this.safePatchValue(value);
      this.previousValueStep01 = value.step01?.currencyPurchaseList ?? null;
      this.previousValueStep02 = value.step02 ?? null;
      this.previousValueStep03 = value.step03 ?? null;
      this.previousValueStep04 = value.step04 ?? null;
      this.previousValueStep05 = value.step05 ?? null;

      if(this.onInit) {
        this.initFormChange();
        this.onInit = false;
      }
    });

    this._translateService.onLangChange.subscribe(event => {
      this._state.changeLanguage(event.lang);
    });

  }

  initFormChange() {
    this.previousValueStep01 = this.getValue(x=>x.step01?.currencyPurchaseList);
    this.previousValueStep02 = this.getValue(x=>x.step02);
    this.previousValueStep03 = this.getValue(x=>x.step03);
    this.previousValueStep04 = this.getValue(x=>x.step04);
    this.previousValueStep05 = this.getValue(x=>x.step05);

    // synchroniser le form -> signal (normalement dans ngonInit)
    var valueChangeFormGroup = this.formGroup?.valueChanges
      .pipe(
          startWith(this.formGroup.value),
          filter(() => !this.isPatching), //ne pas trigger pendant maj du signal
          debounceTime(50)
      )
      .subscribe((current: OrderWizardDto) => {

        switch(current.activeStep){
          case (0): {
            
            const currentCpList = nullifyIfEmpty(current?.step01?.currencyPurchaseList) as CurrencyPurchaseDto[];

            if (!this.isEmpty(currentCpList)) {
              const changeObjectList = this.getDiff(this.previousValueStep01, currentCpList) as ChangeObject<CurrencyPurchaseDto>[];
              const changeObject = this.checkChanges(changeObjectList);
              
              if(changeObject != null) {

                var currentCp = currentCpList[changeObject.index!];
                //begin: TODOhack (voir checkchange)
                  currentCp.sourceAmount = currentCp.sourceAmount==null ? 0 : currentCp.sourceAmount;
                  currentCp.targetAmount = currentCp.targetAmount==null ? 0 : currentCp.targetAmount;
                //end 
                if (changeObject?.changes?.sourceAmount != null) {
                  currentCp.direction = CurrencyDirection.SourceToTarget;
                  
                  this._state.changeItem(changeObject.index!, currentCp);
                }
                else if(changeObject?.changes?.targetAmount != null) {
                  currentCp.direction = CurrencyDirection.TargetToSource;
                 
                  this._state.changeItem(changeObject.index!, currentCp);
                }
                else if(changeObject?.changes.targetCurrency != null) {
                  this._state.changeItem(changeObject.index!, currentCp);
                }
              }
            }
            break;
          }
          case(1): {
            const currentStep02 = nullifyIfEmpty(current?.step02) as PickupPointDto;
            const change = this.getDiff(this.previousValueStep02, currentStep02);

            if(change){
              this._state.patch(current);
            }
            break;
          }
          case(2): {
            const currentStep03 = nullifyIfEmpty(current?.step03) as OrderIdentificationDto;
            const change = this.getDiff(this.previousValueStep03, currentStep03);

            if(change){
              this._state.patch(current);
            }
            break;
          }
          case(3): {
            const currentStep04 = nullifyIfEmpty(current?.step04) as OrderDueDiligenceDto;
            const change = this.getDiff(this.previousValueStep04, currentStep04);

            if(change){
              this._state.patch(current);
            }
            break;
          }
          case(4): {
            // const currentStep05 = nullifyIfEmpty(current?.step05) as OrderGtcDto;
            const currentStep05 = current?.step05 as OrderGtcDto;
            const change = this.getDiff(this.previousValueStep05, currentStep05);

            if(change){
              this._state.patch(current);
            }
            break;
          }

        }

    });
  }

  init(): void {    
    this._state.init();
    this.formGroup?.reset();
    // console.log("form group reset");
  }

  load(orderCode: string): void {
        this._state.load(orderCode);
  }

  initiatePayment() {
    this._state.initiatePayment();
  }

  save(orderWizardDto: OrderWizardDto) {
    this._state.save(orderWizardDto);
  }

  addCurrencyPurchase() {
    // this.onInit = true;
    this._state.addCurrencyPurchase();
  }

  deleteItem(id: number) {
    const formArray = this.getFormArray(x => x.step01?.currencyPurchaseList) as FormArray;
    formArray.removeAt(id);
  }

  changePickupPoint(PickupPointDto: PickupPointDto | null) {
    this._state.changePickupPoint(PickupPointDto);
  }

  updateSession(request: SessionUpdateRequest): Observable<SessionUpdateRequest> {
    return this._state.updateSession(request);
  }

  updateAfterPayment(orderWizardDto: OrderWizardDto) {
    return this._state.updateAfterPayment(orderWizardDto);
  }

  emailValidationSend(customerDto: CustomerDto){
    return this._state.emailValidationSend(customerDto);
  }

  patchCustomer(changes: CustomerDto){
    return this._state.patchCustomer(changes);
  }

  isStepValidated(step: number): boolean {
    // const stepIndex = step-1;
   
    switch(step) {
      case (1): {
        const step01 = this.orderWizard$()?.step01 ?? null;
        const approvalRange = step01?.pricingSummary ?? null;
        const sourceAmount = step01?.currencyPurchaseList?.reduce((sum,item) => sum + Number(item?.sourceAmount ?? 0), 0);
        const serviceFee = step01?.pricingSummary?.serviceFee;

        const exception = sourceAmount!  > (approvalRange?.maxAmount! + serviceFee!) || sourceAmount! < (approvalRange?.minAmount!);
        return !exception;
      }
      case (2):{
        //no selected pickup point
        const hasPickupPoint = this.orderWizard$()?.step02?.place?.id ?? null;
        const hasException = !hasPickupPoint;

        return !hasException;
      }
      case (3): {
        const step03 = this.orderWizard$()?.step03;
        const exceptions = step03?.businessExceptionMessages;

        if(!step03 || (exceptions && exceptions?.length>0) || !step03.customerDto?.isMailConfirmed) {
          return false;
        }
        return true;
      }
      case(4): {
        const step04 = this.orderWizard$()?.step04 ?? null;
        const hasAnswer01 = !(step04?.dueDiligenceDto?.question01?.answer?.id==null);
        const hasAnswer02 = !(step04?.dueDiligenceDto?.question02?.answer?.id==null);  
        const hasAnswer03 = !(step04?.dueDiligenceDto?.question03?.answer?.id==null);

        const code = step04?.dueDiligenceDto?.question03?.answer?.code;

        const q3No = code === 'Q03A02';
        const q3Yes = code === 'Q03A01';
        const hasAdditionalAnswer = !this.isNullOrWhiteSpace(step04?.dueDiligenceDto?.additionalQuestions?.question01?.answer);
        const hasAnswer04 = (q3No && hasAdditionalAnswer) || q3Yes;
        
        const exception = !hasAnswer01 || !hasAnswer02 || !hasAnswer03 || !hasAnswer04;
        return !exception;
      }
    }
    return false;
  }

  isStepCertified(step: number): boolean {
    switch(step) {
      case(4): {
        if(this.isStepValidated(4)){
          const step04 = this.orderWizard$()?.step04 ?? null;
          const isCertified = step04?.dueDiligenceDto?.isInformationCertified ?? false;

          return isCertified;
        }
        return false;
      }
      case(5): {
        const isCertified = this.orderWizard$()?.step05?.isGtcAccepted ?? false; 

        return isCertified;
      }
    }
    return false;
  }

  isNullOrWhiteSpace(value: unknown): boolean {
    return typeof value !== 'string' || !value.trim();
  }

  //check si pas null et un seul item (pas possibilité d'avoir plusieurs item modifié en meme temps)
  checkChanges(value: ChangeObject<CurrencyPurchaseDto>[]): ChangeObject<CurrencyPurchaseDto> | null {
    if(value==null || value ==undefined)
      return null;

  //TODO hack --> a voir comment reinitialiser a null le id de lobjet et non a 0 dans patchevalue du corpForm
  //sourceamount et targetamount si null mettre a 0
const result = value
  .filter(x => {

    if (!x.changes || typeof x.changes !== 'object') {
      return true;
    }

    const keys = Object.keys(x.changes);

    return !(
      keys.length === 1 &&
      keys[0] === 'id' &&
      x.changes.id === null
    );
  })
  .map(x => ({

    ...x,

    changes: {
      ...x.changes,

      ...(x.changes?.sourceAmount === null
        ? { sourceAmount: 0 }
        : {}),

      ...(x.changes?.targetAmount === null
        ? { targetAmount: 0 }
        : {})
    }
  }));



    if(result.length!=1 ){
      return null;
    }

    return result[0];
  }
}
